import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface CountdownTimerProps {
  expiresAt: string; // ISO date string
  onExpire?: () => void;
}

export function CountdownTimer({ expiresAt, onExpire }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;
      return Math.max(0, Math.floor(diff / 1000)); // seconds
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft === 0 && onExpire) {
        onExpire();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const percentage = (timeLeft / (15 * 60)) * 100; // 15 minutes total

  // Color based on time left
  let colorClass = 'text-green-600';
  let bgClass = 'bg-green-600';
  if (percentage < 50) {
    colorClass = 'text-yellow-600';
    bgClass = 'bg-yellow-600';
  }
  if (percentage < 20) {
    colorClass = 'text-red-600';
    bgClass = 'bg-red-600';
  }

  if (timeLeft === 0) {
    return (
      <div className="flex items-center justify-center gap-2 text-red-600 text-sm font-medium">
        <Clock className="w-4 h-4" />
        <span>Code expired</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-center gap-2 text-sm font-medium">
        <Clock className={`w-4 h-4 ${colorClass}`} />
        <span className={colorClass}>
          Expires in {minutes}:{seconds.toString().padStart(2, '0')}
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
        <div 
          className={`h-full transition-all duration-1000 ${bgClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
